import React, { FC, useState } from 'react'
import { DivProps } from '@commonTypes'
import { useThemes } from '@providers/themesProvider'
import { get } from 'lodash'
import cn from 'classnames'
import { Avatar, Icon } from '@ui'
import api, { ICurrentUser } from '@api'
import { legacyMode, themeMode } from 'App'
import { Classes, Menu, MenuDivider, MenuItem, Position } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { useUser } from '@hooks'
import { clearToken } from '@stores/apollo'
import css from './appbar.module.scss'
import { Link, navigate, useLocation } from '@reach/router'
import { Popover } from '@ui'
import BookingsModal from './BookingsModal'
import updateClasic from '@api/legacyMode/updateClassic'
import { logOutAphrodite } from '@utils/navigateToAphrodite'
import { isAnyAdmin } from '@utils'

interface IProps {
  title: string | React.ReactNode
  children?: React.ReactNode
  loading?: boolean
  locationFilterNote?: React.ReactNode
}

export default function Appbar({ title, children = null, loading, locationFilterNote }: IProps) {
  const [showSettings, setShowSettings] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const { user } = useUser()
  const PATH = 'floor_plan'
  const openFeature = () => {
    window.open(user?.company.userVoiceUrl, '_blank')
    setShowSettings(!showSettings)
  }
  const openBookings = () => {
    setOpenDialog(true)
  }
  const location = useLocation()
  const isFloorPlan = () => location?.pathname?.split('/').includes(PATH)

  return (
    <>
      <BookingsModal isOpen={openDialog} onClose={() => setOpenDialog(false)} />
      {!legacyMode && (
        <Section>
          <Tooltip2 content="Knowledge Center" placement={Position.BOTTOM}>
            <a href="https://support.iofficecorp.com/SiQ" target="_blank">
              <Icon name="light fa-circle-question" light={true}></Icon>
            </a>
          </Tooltip2>
          <Tooltip2 content="Settings" placement={Position.BOTTOM}>
            <Link to="/company_settings/user">
              <Icon name="light fa-gear" light={true}></Icon>
            </Link>
          </Tooltip2>
          <Tooltip2 content="My Bookings" placement={Position.BOTTOM}>
            <span>
              <Icon name="light fa-bell" light={true}></Icon>
            </span>
          </Tooltip2>
          <AccountMenu user={user} openFeature={openFeature} openBookings={openBookings} />
        </Section>
      )}
      {!isFloorPlan() && (
        <Container legacyMode={legacyMode}>
          <div className={css.inner}>
            <div className={cn(css.title, { [Classes.SKELETON]: loading })}>
              {title} {locationFilterNote}
            </div>
            {children}
          </div>
        </Container>
      )}
    </>
  )
}

interface IMenuProps {
  openFeature: () => void
  openBookings: () => void
  user: ICurrentUser | null
}
function AccountMenu({ openFeature, openBookings, user }: IMenuProps) {
  const { selectSiq2Theme, selectDefaultTheme } = useThemes()
  if (legacyMode) return null

  const renderThemSettings = () => {
    if (!themeMode) return

    return (
      <>
        <MenuItem text="Default Theme" onClick={selectDefaultTheme} icon="star" />
        <MenuItem text="Siq2 Theme" onClick={selectSiq2Theme} icon="star" />
        <MenuDivider />
      </>
    )
  }
  return (
    <Popover
      position={Position.BOTTOM}
      minimal
      content={
        <Menu>
          {isAnyAdmin(user?.role) && (
            <MenuItem
              text={
                <p>
                  <i className={`fa-light fa-lightbulb-on ${css.menuIcon}`}></i>Suggest a Feature
                </p>
              }
              onClick={openFeature}
            />
          )}
          <MenuItem
            text={
              <p>
                <i className={`fa-light fa-display ${css.menuIcon}`}></i>My Bookings
              </p>
            }
            onClick={openBookings}
          />
          {window.self !== window.top && (
            <MenuItem
              text={
                <p>
                  <i className={`fa-light fa-toggle-on ${css.menuIcon}`}></i>Switch to Classic
                </p>
              }
              onClick={() => {
                updateClasic({ isClassic: true })
                clearToken()
                logOutAphrodite()
              }}
            />
          )}
          <MenuDivider />
          {renderThemSettings()}
          <MenuItem
            text={
              <p>
                <i className={`fa-light fa-right-from-bracket ${css.menuIcon}`}></i>Logout
              </p>
            }
            onClick={() => {
              clearToken()
              if (user) {
                api.auth.logout({ id: user.id })
              }
              window.self === window.top ? (window.location.href = '/login') : logOutAphrodite()
            }}
          />
        </Menu>
      }>
      <Avatar clickable assets={get(user, 'employee.assets')} name={get(user, 'employee.name')} />
    </Popover>
  )
}

interface IContainer {
  legacyMode: boolean
}

const Container: FC<React.PropsWithChildren<IContainer & DivProps>> = ({
  legacyMode,
  className,
  style,
  ...props
}) => {
  return (
    <div
      className={cn(css.container, className)}
      style={{ left: legacyMode ? 0 : 200, ...style }}
      {...props}
    />
  )
}

export const Section: FC<React.PropsWithChildren<DivProps>> = ({ className, ...props }) => {
  return <section className={cn(css.section, className)} {...props} />
}
